import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Link,
  useColorModeValue,
  useToast,
  Icon,
  Select,
  Stack,
  FormHelperText,
  Textarea,
  SimpleGrid,
  Checkbox,
  VStack,
  Progress,
  HStack,
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye, MdLock, MdSchedule } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { authService } from "../../../services/authService";
import menomV2Logo from "../../../assets/img/menomV2Logo.png";
import { TR_CITIES, getDistrictsForCity } from "../../../data/trLocations";

function SignUp() {
  const navigate = useNavigate();
  const toast = useToast();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedKVKK, setAcceptedKVKK] = useState(false);
  // Form için tüm alanları içeren initial state
  const initialFormState = {
    // Business bilgileri
    businessName: '',
    email: '',
    password: '',
    confirmPassword: '',
    ownerName: '',
    ownerPhone: '',
    businessPhone: '',
    description: '',
    businessType: 'RESTAURANT',
    secondaryBusinessType: '',
    plan: 'STANDARD',

    // Adres bilgileri
    address: {
      street: '',
      city: '',
      district: '',
      fullAddress: ''
    },

    // Kart bilgileri (ödeme için)
    paymentCard: {
      cardNumber: '',
      cardHolderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: ''
    },

    // Restaurant (adres senkronu için)
    restaurant: {
      name: '',
      email: '',
      phone: '',
      description: '',
      address: {
        street: '',
        city: '',
        district: '',
        fullAddress: '',
        location: {
          type: 'Point',
          coordinates: [28.9784, 41.0082]
        }
      },
      businessType: 'RESTAURANT',
    }
  };

  const [formData, setFormData] = useState(initialFormState);
  const districts = getDistrictsForCity(formData.address.city);

  // Form seçenekleri
  const businessTypes = [
    // Genel Kategoriler
    { value: 'RESTAURANT', label: 'Restoran' },
    { value: 'CAFE', label: 'Kafe' },
    { value: 'FAST_FOOD', label: 'Fast Food' },
    { value: 'FINE_DINING', label: 'Fine Dining' },
    { value: 'BISTRO', label: 'Bistro' },
    // Spesifik Kategoriler
    { value: 'HAMBURGER', label: 'Hamburgerci' },
    { value: 'PIZZA', label: 'Pizzacı' },
    { value: 'KEBAB', label: 'Kebapçı' },
    { value: 'DONER', label: 'Dönerci' },
    { value: 'LAHMACUN', label: 'Lahmacuncu' },
    { value: 'PIDE', label: 'Pideci' },
    { value: 'BREAKFAST', label: 'Kahvaltıcı' },
    { value: 'DESSERT', label: 'Tatlıcı' },
    { value: 'ICE_CREAM', label: 'Dondurmacı' },
    { value: 'BAKERY', label: 'Fırın' },
    { value: 'BAR', label: 'Bar' },
    { value: 'PUB', label: 'Pub' },
    { value: 'TEA_HOUSE', label: 'Çay Evi' },
    { value: 'HOOKAH', label: 'Nargile' }
  ];

  const DEFAULT_SIGNUP_WORKING_HOURS = {
    monday: { isOpen: true, open: '09:00', close: '22:00' },
    tuesday: { isOpen: true, open: '09:00', close: '22:00' },
    wednesday: { isOpen: true, open: '09:00', close: '22:00' },
    thursday: { isOpen: true, open: '09:00', close: '22:00' },
    friday: { isOpen: true, open: '09:00', close: '22:00' },
    saturday: { isOpen: true, open: '09:00', close: '22:00' },
    sunday: { isOpen: true, open: '09:00', close: '22:00' },
  };


  // Chakra Color Mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.300");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const cardBg = useColorModeValue("white", "navy.800");
  const sectionBg = useColorModeValue("gray.50", "navy.900");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const inputBg = useColorModeValue("white", "navy.700");
  const paymentOverlayBg = useColorModeValue(
    "rgba(255, 255, 255, 0.7)",
    "rgba(26, 32, 44, 0.7)"
  );

  const handleClick = () => setShow(!show);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      setFormData(prev => {
        if (subChild) {
          // 3 seviyeli nested (restaurant.socialMedia.instagram)
          return {
            ...prev,
            [parent]: {
              ...(prev[parent] || {}),
              [child]: {
                ...(prev[parent]?.[child] || {}),
                [subChild]: value
              }
            }
          };
        }
        // 2 seviyeli nested (restaurant.website)
        return {
          ...prev,
          [parent]: {
            ...(prev[parent] || {}),
            [child]: value
          }
        };
      });

      // Restaurant bilgilerini otomatik güncelle
      if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          restaurant: {
            ...prev.restaurant,
            address: {
              ...prev.restaurant.address,
              [child]: value
            }
          }
        }));
      }
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value,
        };

        // Restaurant bilgilerini otomatik güncelle
        if (name === 'description') {
          newData.restaurant = {
            ...prev.restaurant,
            description: value
          };
        }
        if (name === 'businessType') {
          newData.restaurant = {
            ...prev.restaurant,
            businessType: value
          };
        }

        return newData;
      });
    }
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city,
        district: '' // şehir değişince ilçeyi sıfırla
      },
      restaurant: {
        ...prev.restaurant,
        address: {
          ...prev.restaurant.address,
          city,
          district: ''
        }
      }
    }));
  };

  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        district
      },
      restaurant: {
        ...prev.restaurant,
        address: {
          ...prev.restaurant.address,
          district
        }
      }
    }));
  };

  // Şifre validasyon fonksiyonu
  const validatePassword = (password) => {
    if (!password || password.length < 8) {
      return 'Şifre en az 8 karakter olmalıdır';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Şifre en az 1 büyük harf içermelidir';
    }
    if (!/[a-z]/.test(password)) {
      return 'Şifre en az 1 küçük harf içermelidir';
    }
    if (!/[0-9]/.test(password)) {
      return 'Şifre en az 1 rakam içermelidir';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Şifre en az 1 özel karakter içermelidir (!@#$%^&*...)';
    }
    return null;
  };

  const steps = [
    { key: 'account', title: 'Hesap & İşletme' },
    { key: 'finish', title: 'Adres, Ödeme & Onay' },
  ];

  const validateStep = (currentStep) => {
    if (currentStep === 0) {
      const passwordError = validatePassword(formData.password);
      if (passwordError) return passwordError;
      if (formData.password !== formData.confirmPassword) return 'Şifreler eşleşmiyor';

      const required = [
        ['businessName', 'İşletme adı'],
        ['email', 'Email'],
        ['description', 'İşletme açıklaması'],
        ['businessType', 'Birincil işletme türü'],
        ['plan', 'Paket seçimi'],
        ['ownerName', 'İşletme sahibi adı soyadı'],
        ['ownerPhone', 'İşletme sahibi telefonu'],
        ['businessPhone', 'İşletme telefonu'],
      ];
      for (const [field, label] of required) {
        if (!String(formData[field] ?? '').trim()) return `${label} zorunludur`;
      }
      const sec = String(formData.secondaryBusinessType ?? '').trim();
      if (sec && sec === formData.businessType) {
        return 'İkincil tür, birincil tür ile aynı olamaz';
      }
      return null;
    }

    if (currentStep === 1) {
      const required = [
        ['city', 'Şehir'],
        ['district', 'İlçe'],
        ['fullAddress', 'Tam adres'],
      ];
      for (const [field, label] of required) {
        if (!String(formData.address?.[field] ?? '').trim()) return `${label} zorunludur`;
      }
      return null;
    }

    return null;
  };

  const goNext = () => {
    const error = validateStep(step);
    if (error) {
      toast({
        title: "Eksik Bilgi",
        description: error,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Şifre validasyonu
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      toast({
        title: "Geçersiz Şifre",
        description: passwordError,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Şifre eşleşme kontrolü
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Şifre Uyuşmazlığı",
        description: "Şifreler eşleşmiyor",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Sözleşme kontrolleri
    if (!acceptedTerms || !acceptedKVKK) {
      toast({
        title: "Eksik Bilgi",
        description: "Lütfen tüm sözleşmeleri kabul edin",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const submitData = new FormData();

    try {
      setLoading(true);

      // Business temel bilgilerini ekle
      const businessFields = {
        businessName: formData.businessName,
        email: formData.email,
        password: formData.password,
        ownerName: formData.ownerName,
        ownerPhone: formData.ownerPhone,
        businessPhone: formData.businessPhone,
        description: formData.description,
        businessType: formData.businessType,
        secondaryBusinessType: formData.secondaryBusinessType || '',
        plan: formData.plan || 'STANDARD'
      };

      // Business bilgilerini FormData'ya ekle
      Object.entries(businessFields).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Address bilgilerini ekle (koordinatları içerecek şekilde)
      const addressData = {
        street: formData.address.street,
        city: formData.address.city,
        district: formData.address.district,
        fullAddress: formData.address.fullAddress
      };
      submitData.append('address', JSON.stringify(addressData));

      submitData.append('workingHours', JSON.stringify(DEFAULT_SIGNUP_WORKING_HOURS));

      // Restaurant verilerini ekle
      const restaurantData = {
        name: formData.businessName,
        email: formData.email,
        phone: formData.businessPhone,
        description: formData.description,
        address: addressData,
        businessType: formData.businessType,
      };
      submitData.append('restaurant', JSON.stringify(restaurantData));

      // Kart bilgilerini ekle
      if (formData.paymentCard.cardNumber && formData.paymentCard.cardHolderName) {
        const cardData = {
          cardNumber: formData.paymentCard.cardNumber.replace(/\s/g, ''), // Boşlukları temizle
          cardHolderName: formData.paymentCard.cardHolderName,
          expiryMonth: formData.paymentCard.expiryMonth,
          expiryYear: formData.paymentCard.expiryYear,
          cvv: formData.paymentCard.cvv
        };
        submitData.append('paymentCard', JSON.stringify(cardData));
      }

      const result = await authService.register(submitData);
      
      if (result.success) {
        toast({
          title: "Başarılı!",
          description: "Kaydınız başarıyla oluşturuldu.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate('/auth/sign-in');
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast({
        title: "Hata!",
        description: error.message || "Kayıt işlemi sırasında bir hata oluştu.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Gradient Arka Plan */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex={0}
        bgGradient="linear(to-br, brand.100, purple.50)"
        opacity={0.5}
        pointerEvents="none"
      />

      {/* Logo - Sağ Üst */}
      <Box
        position="fixed"
        top={{ base: "20px", md: "30px" }}
        right={{ base: "20px", md: "40px" }}
        zIndex={2}
      >
        <img 
          src={menomV2Logo} 
          alt="Menom Logo" 
          style={{ 
            height: '50px',
            width: 'auto',
            objectFit: 'contain'
          }} 
        />
      </Box>

      <Flex
        direction="column"
        alignSelf="center"
        justifySelf="center"
        overflow="hidden"
        position="relative"
        zIndex={1}
      >
        <Flex
          direction="column"
          textAlign="center"
          justifyContent="center"
          align="center"
          mt="6.5rem"
          mb="40px"
        >
          <Heading 
            color={textColor} 
            fontSize={{ base: "28px", md: "36px" }} 
            mb="12px"
            fontWeight="700"
            letterSpacing="-0.5px"
          >
            İşletmenizi Kaydedin
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize={{ base: "sm", md: "md" }}
            maxW="500px"
          >
            Hemen hesap oluşturun! Menom'a katılın!
          </Text>
          <Text fontSize="xs" color={textColorSecondary} maxW="520px" mt="-4" mb="2">
            Kayıt süresi oldukça kısadır. Giriş yaptıktan sonra müşterilierinizin sizi daha yakından tanıyabilmeleri için profilinizi tamamlamayı unutmayınız! 
          </Text>
        </Flex>
        <Flex alignItems="center" justifyContent="center" mb="60px" mt="20px">
          <Flex
            direction="column"
            w={{ base: "100%", md: "1200px" }}
            background="transparent"
            borderRadius="20px"
            p={{ base: "30px", md: "50px" }}
            mx={{ base: "20px", md: "100px" }}
            bg={cardBg}
            boxShadow="0 20px 60px 0 rgba(0, 0, 0, 0.08)"
            border="1px solid"
            borderColor={borderColor}
          >
            <FormControl>
              {/* Stepper */}
              <Box mb={8}>
                <HStack
                  justify={{ base: "center", md: "space-between" }}
                  mb={2}
                  flexWrap="wrap"
                  rowGap={1}
                >
                  <Text fontWeight="700" color={textColor} textAlign="center" w={{ base: "100%", md: "auto" }}>
                    {steps[step]?.title}
                  </Text>
                  <Text fontSize="sm" color={textColorSecondary} textAlign="center" w={{ base: "100%", md: "auto" }}>
                    {step + 1}/{steps.length}
                  </Text>
                </HStack>
                <Progress
                  value={((step + 1) / steps.length) * 100}
                  borderRadius="full"
                  size="sm"
                  colorScheme="red"
                  w="100%"
                />
              </Box>

              {step === 0 && (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                <Box
                  p={6}
                  borderRadius="12px"
                  bg={sectionBg}
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Heading 
                    size="sm" 
                    mb={6}
                    color={textColor}
                    fontWeight="600"
                    fontSize="16px"
                    pb={2}
                    borderBottom="2px solid"
                    borderColor={brandStars}
                  >
                    Hesap Bilgileri
                  </Heading>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İşletme Adı<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input 
                      name="businessName" 
                      value={formData.businessName} 
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      Email<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                  <FormLabel display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px">
                    Şifre<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <InputGroup size="md" mb="8px">
                    <Input
                      type={show ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                    <InputRightElement>
                      <Icon
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                        cursor="pointer"
                        color={textColorSecondary}
                        _hover={{ color: brandStars }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText fontSize="xs" color={textColorSecondary} mb="16px">
                    Şifre en az 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter içermelidir
                  </FormHelperText>

                  <FormLabel display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px">
                    Şifre Tekrar<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <InputGroup size="md" mb="24px">
                    <Input
                      type={show ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                    <InputRightElement>
                      <Icon
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                        cursor="pointer"
                        color={textColorSecondary}
                        _hover={{ color: brandStars }}
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>

                <Box
                  p={6}
                  borderRadius="12px"
                  bg={sectionBg}
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Heading 
                    size="sm" 
                    mb={6}
                    color={textColor}
                    fontWeight="600"
                    fontSize="16px"
                    pb={2}
                    borderBottom="2px solid"
                    borderColor={brandStars}
                  >
                    İşletme Bilgileri
                  </Heading>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İşletme Açıklaması<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="İşletmenizi kısaca tanıtın..."
                      maxLength={500}
                      rows={4}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                    <FormHelperText fontSize="xs" mt={1}>
                      {formData.description.length}/500 karakter
                    </FormHelperText>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      Birincil işletme türü<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    >
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İkincil işletme türü <Text as="span" fontWeight="400" color={textColorSecondary}>(opsiyonel)</Text>
                    </FormLabel>
                    <Select
                      name="secondaryBusinessType"
                      value={formData.secondaryBusinessType}
                      onChange={handleChange}
                      placeholder="Hiçbiri"
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    >
                      <option value="">—</option>
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      Paket Seçimi<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Select
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    >
                      <option value="STANDARD">STANDARD</option>
                      <option value="PREMIUM">PREMIUM</option>
                    </Select>
                  </FormControl>

                </Box>

                <Box
                  p={6}
                  borderRadius="12px"
                  bg={sectionBg}
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Heading 
                    size="sm" 
                    mb={6}
                    color={textColor}
                    fontWeight="600"
                    fontSize="16px"
                    pb={2}
                    borderBottom="2px solid"
                    borderColor={brandStars}
                  >
                    İletişim Bilgileri
                  </Heading>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İşletme Sahibinin Adı Soyadı<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input 
                      name="ownerName" 
                      value={formData.ownerName} 
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İşletme Sahibinin Telefonu<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input
                      name="ownerPhone"
                      value={formData.ownerPhone}
                      onChange={handleChange}
                      placeholder="İşletme Sahibi Telefonu"
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px">
                      İşletme Telefonu<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input
                      name="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleChange}
                      placeholder="İşletme Telefonu"
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>
                </Box>
              </SimpleGrid>
              )}

              {step === 1 && (
              <>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mt={8} alignItems="stretch">
                <Box
                  p={6}
                  borderRadius="12px"
                  bg={sectionBg}
                  border="1px solid"
                  borderColor={borderColor}
                  h="100%"
                >
                  <Heading 
                    size="sm" 
                    mb={6}
                    color={textColor}
                    fontWeight="600"
                    fontSize="16px"
                    pb={2}
                    borderBottom="2px solid"
                    borderColor={brandStars}
                  >
                    Adres Bilgileri
                  </Heading>
                  
                  <FormControl mb={4}>
                    <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                      Sokak
                    </FormLabel>
                    <Input 
                      name="address.street" 
                      value={formData.address.street} 
                      onChange={handleChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                      Şehir<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Select
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleCityChange}
                      placeholder="Şehir seçin"
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    >
                      {TR_CITIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                      İlçe<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Select
                      name="address.district"
                      value={formData.address.district}
                      onChange={handleDistrictChange}
                      placeholder={formData.address.city ? "İlçe seçin" : "Önce şehir seçin"}
                      isDisabled={!formData.address.city}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    >
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                      Tam Adres<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Textarea
                      name="address.fullAddress"
                      value={formData.address.fullAddress}
                      onChange={handleChange}
                      rows={3}
                      bg={inputBg}
                      borderColor={borderColor}
                      _hover={{ borderColor: brandStars }}
                      _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                    />
                  </FormControl>

                </Box>

                <Box
                  p={6}
                  borderRadius="12px"
                  bg={sectionBg}
                  border="1px solid"
                  borderColor={borderColor}
                  position="relative"
                  h="100%"
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg={paymentOverlayBg}
                    backdropFilter="blur(4px)"
                    zIndex={10}
                    borderRadius="12px"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={8}
                  >
                    <Icon as={MdLock} boxSize={16} color="gray.400" mb={4} />
                    <Flex align="center" gap={2} mb={2}>
                      <Icon as={MdSchedule} color="orange.500" />
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        Yakında Eklenecek
                      </Text>
                    </Flex>
                    <Text color={textColorSecondary} textAlign="center" maxW="400px">
                      Ödeme bilgileri özelliği yakında aktif edilecektir. Bu özellik sayesinde kart bilgilerinizi kaydedebilecek ve otomatik ödeme yapabileceksiniz.
                    </Text>
                  </Box>

                  <Box opacity={0.5} pointerEvents="none">
                    <Heading
                      size="sm"
                      mb={6}
                      color={textColor}
                      fontWeight="600"
                      fontSize="16px"
                      pb={2}
                      borderBottom="2px solid"
                      borderColor={brandStars}
                    >
                      Ödeme Kartı Bilgileri
                    </Heading>
                    <FormHelperText mb={6} fontSize="sm" color={textColorSecondary}>
                      Aylık ücret ve komisyon farkı tahsilatı için kart bilgileriniz gereklidir. Kart bilgileriniz güvenli bir şekilde saklanır.
                    </FormHelperText>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl mb={4}>
                        <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                          Kart Numarası<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="paymentCard.cardNumber"
                          value={formData.paymentCard.cardNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                            const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                            setFormData(prev => ({
                              ...prev,
                              paymentCard: {
                                ...prev.paymentCard,
                                cardNumber: formatted
                              }
                            }));
                          }}
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                          bg={inputBg}
                          borderColor={borderColor}
                          _hover={{ borderColor: brandStars }}
                          _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                        />
                      </FormControl>

                      <FormControl mb={4}>
                        <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                          Kart Sahibi Adı<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="paymentCard.cardHolderName"
                          value={formData.paymentCard.cardHolderName}
                          onChange={handleChange}
                          placeholder="AD SOYAD"
                          bg={inputBg}
                          borderColor={borderColor}
                          _hover={{ borderColor: brandStars }}
                          _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                        />
                      </FormControl>

                      <FormControl mb={4}>
                        <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                          Son Kullanma Tarihi<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Flex gap={2}>
                          <Select
                            name="paymentCard.expiryMonth"
                            value={formData.paymentCard.expiryMonth}
                            onChange={handleChange}
                            placeholder="Ay"
                            bg={inputBg}
                            borderColor={borderColor}
                            _hover={{ borderColor: brandStars }}
                            _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                              <option key={month} value={month.toString().padStart(2, '0')}>
                                {month.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </Select>
                          <Select
                            name="paymentCard.expiryYear"
                            value={formData.paymentCard.expiryYear}
                            onChange={handleChange}
                            placeholder="Yıl"
                            bg={inputBg}
                            borderColor={borderColor}
                            _hover={{ borderColor: brandStars }}
                            _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                          >
                            {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                              <option key={year} value={year.toString().slice(-2)}>
                                {year}
                              </option>
                            ))}
                          </Select>
                        </Flex>
                      </FormControl>

                      <FormControl mb={4}>
                        <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                          CVV<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="paymentCard.cvv"
                          value={formData.paymentCard.cvv}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setFormData(prev => ({
                              ...prev,
                              paymentCard: {
                                ...prev.paymentCard,
                                cvv: value
                              }
                            }));
                          }}
                          placeholder="123"
                          maxLength={3}
                          type="password"
                          bg={inputBg}
                          borderColor={borderColor}
                          _hover={{ borderColor: brandStars }}
                          _focus={{ borderColor: brandStars, boxShadow: `0 0 0 1px ${brandStars}` }}
                        />
                      </FormControl>
                    </SimpleGrid>
                  </Box>
                </Box>
              </SimpleGrid>

              <Box
                mt={8}
                w="100%"
                p={{ base: 5, md: 8 }}
                borderRadius="12px"
                bg={sectionBg}
                border="1px solid"
                borderColor={borderColor}
              >
                <Heading
                  size="sm"
                  mb={4}
                  color={textColor}
                  fontWeight="600"
                  fontSize="16px"
                  pb={2}
                  borderBottom="2px solid"
                  borderColor={brandStars}
                >
                  Onaylar
                </Heading>
                <FormHelperText mb={6} fontSize="sm" color={textColorSecondary}>
                  Başvurunuz incelendikten sonra onaylanacaktır.
                  Bu süreçte bilgilendirileceksiniz.
                </FormHelperText>

                <VStack align="stretch" spacing={4} mb={6}>
                  <Checkbox
                    isChecked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    colorScheme="brand"
                    size="md"
                  >
                    <Text fontSize="sm" color={textColor}>
                      <Link href="/terms?type=business" target="_blank" color="brand.500" textDecoration="underline" _hover={{ color: brandStars }}>
                        Kullanım Sözleşmesi
                      </Link>
                      {' '}ni okudum ve kabul ediyorum.
                    </Text>
                  </Checkbox>

                  <Checkbox
                    isChecked={acceptedKVKK}
                    onChange={(e) => setAcceptedKVKK(e.target.checked)}
                    colorScheme="brand"
                    size="md"
                  >
                    <Text fontSize="sm" color={textColor}>
                      <Link href="/kvkk?type=business" target="_blank" color="brand.500" textDecoration="underline" _hover={{ color: brandStars }}>
                        Aydınlatma Metni ve KVKK Sözleşmesi
                      </Link>
                      {' '}ni okudum ve kabul ediyorum.
                    </Text>
                  </Checkbox>
                </VStack>

                <Button
                  fontSize="md"
                  variant="brand"
                  fontWeight="600"
                  w="100%"
                  maxW="100%"
                  h="56px"
                  mb={6}
                  onClick={handleSubmit}
                  isLoading={loading}
                  borderRadius="12px"
                  boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)" }}
                  transition="all 0.2s"
                >
                  Kayıt Ol
                </Button>

                <Flex justifyContent="center" alignItems="center">
                  <Text color={textColorSecondary} fontWeight="400" fontSize="14px">
                    Zaten hesabınız var mı?{" "}
                    <Link
                      as={RouterLink}
                      to="/auth/sign-in"
                      color="brand.500"
                      fontWeight="500"
                    >
                      Giriş Yapın
                    </Link>
                  </Text>
                </Flex>
              </Box>

              </>
              )}

              {/* Navigation Buttons */}
              <HStack justify="space-between" mt={8}>
                <Button
                  onClick={goBack}
                  isDisabled={step === 0}
                  variant="outline"
                  borderRadius="12px"
                >
                  Geri
                </Button>
                {step < steps.length - 1 ? (
                  <Button
                    onClick={goNext}
                    variant="brand"
                    borderRadius="12px"
                  >
                    Devam
                  </Button>
                ) : null}
              </HStack>
            </FormControl>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}

export default SignUp; 